/**
 * The music button in the viewer HUD: icon-only in the desktop dock (same
 * look as its neighbours), a "Music" item in the phone bar's More sheet.
 * Small on purpose — the popover and runtime are lazy-loaded.
 */
import React from 'react';
import { Music, VolumeX } from 'lucide-react';
import { useMusicStore, openMusicPanel, hasAnyMusic } from '../../services/music/musicStore';

/** Show the button: there's music here, or you could add some. */
export function useShowMusicButton(): boolean {
  return useMusicStore((s) => hasAnyMusic(s) || s.isHost);
}

export const MusicDockButton: React.FC = () => {
  const show = useShowMusicButton();
  const open = useMusicStore((s) => s.panelOpen);
  const muted = useMusicStore((s) => s.muted);
  const blocked = useMusicStore((s) => s.blocked);
  const playing = useMusicStore((s) => !!s.world?.playing || s.speakers.some((sp) => sp.playing));
  if (!show) return null;
  const tip = muted ? 'Music (muted)' : blocked ? 'Music — tap to start' : 'Music';
  return (
    <button
      type="button"
      className={`gx-dock__btn${open ? ' is-on' : ''}${playing && !muted ? ' is-accent' : ''}${blocked && !muted ? ' is-warn' : ''}`}
      onClick={() => openMusicPanel()}
      aria-label={tip}
      aria-expanded={open}
      data-tip={tip}
      data-music="open"
    >
      {muted ? <VolumeX size={18} /> : <Music size={18} />}
    </button>
  );
};

/** Entry for ViewerHudBar's More sheet (phones / narrow windows). */
export function useMusicHudTool(): { key: string; icon: React.ReactNode; label: string; onClick: () => void; tone?: 'accent' | 'warn' } | null {
  const show = useShowMusicButton();
  const muted = useMusicStore((s) => s.muted);
  const blocked = useMusicStore((s) => s.blocked);
  if (!show) return null;
  return {
    key: 'music',
    icon: muted ? <VolumeX size={17} /> : <Music size={17} />,
    label: muted ? 'Music (muted)' : 'Music',
    onClick: () => openMusicPanel(true),
    ...(blocked && !muted ? { tone: 'warn' as const } : {}),
  };
}

/** Builder header: same look as its neighbours (btn-glass). */
export const BuilderMusicButton: React.FC = () => {
  const show = useShowMusicButton();
  const open = useMusicStore((s) => s.panelOpen);
  const muted = useMusicStore((s) => s.muted);
  const blocked = useMusicStore((s) => s.blocked);
  if (!show) return null;
  return (
    <button type="button" className={`btn-glass ${open ? 'active' : ''}`} onClick={() => openMusicPanel()} aria-expanded={open}
      title={muted ? 'Music (muted)' : blocked ? 'Music — click to start' : 'Music: your volume, and playback for hosts'}>
      {muted ? <VolumeX size={14} /> : <Music size={14} />}<span>Music</span>
    </button>
  );
};
